import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Star, Heart, Image, Hammer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Stars = ({ count }) => <span className="flex">{[1,2,3,4,5].map(i => <Star key={i} className={`h-3 w-3 ${i <= count ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`} />)}</span>;

const CollectionDetail = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const collectionId = params.collectionId || searchParams.get('collectionId');
  const navigate = useNavigate();
  const [col, setCol] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/diy-collections/${collectionId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        setCol(res.data);
      } catch { setCol(null); }
      setLoading(false);
    };
    fetch();
  }, [collectionId]);

  if (loading) return <div className="bg-amber-50 flex items-center justify-center text-stone-400">Loading...</div>;
  if (!col) return (
    <div className="bg-amber-50 flex items-center justify-center">
      <div className="text-center space-y-3">
        <p className="text-stone-500">Collection not found.</p>
        <button onClick={() => navigate('/?view=diy-ideas')} className="text-amber-600 text-sm font-medium hover:underline">Back to DIY Ideas</button>
      </div>
    </div>
  );

  const getPhotoPath = (p) => typeof p === 'string' ? p : p?.path || '';

  return (
    <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 pb-24">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-stone-600 hover:text-stone-800 mb-4 text-sm">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 mb-6">
          <h1 className="text-xl font-bold text-stone-800">{col.name}</h1>
          {col.description && <p className="text-sm text-stone-500 mt-1">{col.description}</p>}
          <p className="text-xs text-stone-400 mt-2">by {col.author_name || 'Anonymous'} — {(col.idea_ids || []).length} ideas</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(col.ideas || []).map(idea => (
            <div key={idea.id} onClick={() => navigate('/?view=diy-ideas')} className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden hover:shadow-md transition cursor-pointer">
              {(idea.photos || []).length > 0 ? (
                <img src={`${API}/files/${getPhotoPath(idea.photos[0])}`} alt="" className="w-full h-36 object-cover" />
              ) : (
                <div className="w-full h-24 bg-amber-50 flex items-center justify-center"><Image className="h-8 w-8 text-amber-300" /></div>
              )}
              <div className="p-3">
                <h3 className="font-bold text-sm text-stone-800 mb-1">{idea.title}</h3>
                {idea.review_count > 0 && <div className="flex items-center gap-1"><Stars count={Math.round(idea.review_avg)} /><span className="text-xs text-stone-500">{idea.review_avg}</span></div>}
                <div className="flex items-center gap-2 mt-1 text-xs text-stone-400">
                  <span><Heart className="h-3 w-3 inline" /> {idea.likes || 0}</span>
                  {idea.made_it_count > 0 && <span><Hammer className="h-3 w-3 inline" /> {idea.made_it_count}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
        {(col.ideas || []).length === 0 && <p className="text-sm text-stone-400 italic text-center py-8">No ideas in this collection yet</p>}
      </div>
    </div>
  );
};

export default CollectionDetail;
